/**
 * ASCAUTO — Validación de política de contraseñas en frontend (Iter 134+).
 *
 * Espejo en cliente de `/app/backend/security/password_policy.py`.
 * El servidor SIEMPRE valida; esto es solo UX y feedback en vivo.
 */

const MIN_LENGTH_DEFAULT = 12;
const MIN_LENGTH_ADMIN = 14;
const MAX_LENGTH = 128;
const PRIVILEGED_ROLES = new Set(["super_admin", "admin"]);

const COMMON_PASSWORDS = new Set([
  "123456", "1234567", "12345678", "123456789", "1234567890",
  "111111", "000000", "121212", "abc123", "qwerty", "qwerty123",
  "asdfgh", "asdfghjkl", "zxcvbn", "qazwsx",
  "password", "password1", "password123", "passw0rd", "p@ssword", "p@ssw0rd",
  "admin", "admin123", "administrator", "root", "toor", "superadmin",
  "ascauto", "ascauto123", "ascauto2025", "ascauto2026", "ascauto1",
  "taller", "taller123", "taller2025", "stellantis", "stellantis123",
  "test", "test1234", "test123", "prueba", "prueba123",
  "contrasena", "contraseña", "contrasena123", "contraseña123",
  "iloveyou", "letmein", "welcome", "welcome1", "monkey", "dragon",
  "master", "shadow", "football", "baseball",
  "user", "user123", "demo", "demo123", "guest", "guest123",
]);

const _normalize = (text) => {
  if (!text) return "";
  return String(text).normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "").toLowerCase();
};

const _extractTokens = (userData) => {
  const data = userData || {};
  const out = new Set(["ascauto", "taller", "stellantis"]);
  const rawFields = [
    data.name, data.first_name, data.last_name, data.email,
    data.login, data.phone, data.plate,
    data.department, data.department_name, data.company_name,
  ];
  for (const raw of rawFields) {
    if (!raw) continue;
    const norm = _normalize(raw);
    if (norm.includes("@")) {
      const [local, domain = ""] = norm.split("@");
      if (local) out.add(local);
      if (domain) out.add(domain.split(".")[0]);
    }
    for (const piece of norm.split(/[\s._\-@]+/)) {
      const p = piece.trim();
      if (p.length >= 4) out.add(p);
    }
    if (norm.length >= 4) out.add(norm);
  }
  return Array.from(out).filter(Boolean);
};

export function getMinLength(role) {
  return role && PRIVILEGED_ROLES.has(role) ? MIN_LENGTH_ADMIN : MIN_LENGTH_DEFAULT;
}

export const PASSWORD_MAX_LENGTH = MAX_LENGTH;

/**
 * Evalúa la fortaleza. Devuelve {score 0-4, label, reasons[], min_length, is_valid}.
 * Misma semántica que el backend.
 */
export function evaluatePassword(password, { userData, role } = {}) {
  const pwd = password || "";
  const minLen = getMinLength(role);
  const reasons = [];
  let score = 0;

  const L = pwd.length;
  if (L === 0) {
    return { score: 0, label: "Débil",
      reasons: ["Introduce una contraseña."],
      min_length: minLen, is_valid: false };
  }
  if (L < minLen) {
    reasons.push(`Demasiado corta (mínimo ${minLen} caracteres).`);
  } else {
    score += 1 + Math.min(2, Math.floor((L - minLen) / 4));
  }
  if (L > MAX_LENGTH) {
    reasons.push(`Demasiado larga (máximo ${MAX_LENGTH} caracteres).`);
  }

  const hasLower = /[a-zñáéíóúü]/i.test(pwd) && /[a-zñáéíóúü]/.test(pwd);
  const hasUpper = /[A-ZÑÁÉÍÓÚÜ]/.test(pwd);
  const hasDigit = /[0-9]/.test(pwd);
  const hasSymbol = /[^A-Za-z0-9\s]/.test(pwd);
  const diversity = [hasLower, hasUpper, hasDigit, hasSymbol].filter(Boolean).length;
  if (diversity >= 3) score += 1;
  else if (diversity === 2 && L >= minLen + 4) score += 1;

  if (/^\d+$/.test(pwd)) {
    reasons.push("No uses solo números.");
    score = Math.min(score, 1);
  }
  if (new Set(pwd).size <= 3 && L >= 6) {
    reasons.push("Demasiados caracteres repetidos.");
    score = Math.min(score, 1);
  }

  const norm = _normalize(pwd);
  if (COMMON_PASSWORDS.has(norm)) {
    reasons.push("Muy común y conocida en filtraciones.");
    score = 0;
  } else {
    for (const c of COMMON_PASSWORDS) {
      if (c.length >= 5 && norm.includes(c) && L <= c.length + 4) {
        reasons.push(`Contiene un patrón muy común («${c}»).`);
        score = Math.min(score, 1);
        break;
      }
    }
  }

  const tokens = _extractTokens(userData);
  for (const tok of tokens) {
    if (tok && tok.length >= 4 && norm.includes(tok)) {
      reasons.push(`No incluyas datos personales («${tok}»).`);
      score = Math.min(score, 1);
      break;
    }
  }

  const blocking = ["Demasiado corta", "Muy común", "datos personales",
    "patrón muy común", "solo números", "caracteres repetidos", "Demasiado larga"];
  const isValid = !reasons.some(r => blocking.some(b => r.includes(b)));
  if (!isValid) score = 0;

  const labels = ["Débil", "Aceptable", "Buena", "Fuerte", "Muy fuerte"];
  let label = labels[Math.min(score, 4)];
  if (!isValid) label = "Débil";

  if (isValid && L < minLen + 4 && diversity < 3) {
    reasons.push("Sugerencia: usa una frase más larga o añade variedad.");
  }

  return {
    score: Math.max(0, Math.min(score, 4)),
    label, reasons, min_length: minLen, is_valid: isValid,
  };
}
